const MATERIAL_SYMBOL_SELECTOR = [
  ".material-symbols-outlined",
  ".material-symbols-rounded",
  ".material-symbols-sharp",
].join(",");

function protectMaterialSymbol(element: Element) {
  element.setAttribute("translate", "no");
  element.classList.add("notranslate");
}

/**
 * Material Symbols use ligature names such as `visibility` as their text
 * content. Browser translators must not translate those implementation tokens,
 * otherwise the ligature breaks and the token is rendered as visible copy.
 *
 * The observer is intentionally limited to newly inserted nodes. It covers
 * lazy routes, drawers and virtualized rows without watching every text or
 * class update in the application.
 */
export function installMaterialSymbolTranslationGuard(
  root: Document | Element = document,
): () => void {
  const protectTree = (node: Document | Element) => {
    if (node instanceof Element && node.matches(MATERIAL_SYMBOL_SELECTOR)) {
      protectMaterialSymbol(node);
    }

    node.querySelectorAll(MATERIAL_SYMBOL_SELECTOR).forEach(protectMaterialSymbol);
  };

  protectTree(root);

  const observationRoot = root instanceof Document ? root.documentElement : root;
  if (!observationRoot) return () => undefined;

  const observer = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node instanceof Element) protectTree(node);
      });
    });
  });

  observer.observe(observationRoot, { childList: true, subtree: true });

  return () => observer.disconnect();
}
